import { useEffect, useId, useState } from 'react';
import {
  MEDIA_RULES,
  type PanelOrganizationCoverRemovalDto,
  type PanelOrganizationDetailDto,
} from '@icebreaker/shared';
import { Avatar, Button } from '@icebreaker/ui';
import { subSurfacePermission } from '@/app/screen-permissions';
import { PermissionSurfaceGate } from '@/features/access/ui/permission-gate';
import { numericDateTimeOptions } from '@/features/sales/model/sales-view';
import { useTranslation } from '@/shared/i18n/i18n-provider';
import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';
import type { RootState } from '@/shared/store/store';
import { coverRemovalReset } from '../store/organizations.slice';
import { RemoveCoverDialog } from './remove-cover-dialog';
import styles from './organizations.module.css';

/** Önizleme kutusu kurum sayfasının kapak oranındadır (yükleme kuralıyla aynı kaynak). */
const COVER = MEDIA_RULES.recommendedCoverDimensions.organization_cover;
const COVER_RATIO = { aspectRatio: `${COVER.width} / ${COVER.height}` };

/**
 * KAPAK GÖRSELİ (kurum kapağı · Story P1): kurumun herkese açık kapağının
 * önizlemesi ve gerekçeli "Kapağı kaldır". Önizleme kurum ekranını görebilen
 * her personele çizilir; eylem AYRI yetkidir (`panel:organization-cover-removal`):
 * yetkisi olmayan personelde düğme çizilmez, yerine alt yüzeylerin tek satırlık
 * dürüst notu kalır (sahiplenme karar panosuyla aynı desen).
 *
 * Kapak yoksa tek satırlık not; türevler henüz üretilmediyse ya da adres
 * yüklenemezse aynı oranda sakin kutu ve tek cümle (kırık görsel çizilmez).
 *
 * SON KALDIRMA (karar 14.10.2026): personel daha önce kapak kaldırdıysa kartın
 * altında okunur gerekçe, kaldıran (avatar + ad) ve tarih durur. Kapak yeniden
 * yüklense de kalır; hiç kaldırma yoksa bölüm çizilmez.
 */
export function OrganizationCoverCard({ org }: { org: PanelOrganizationDetailDto }) {
  const t = useTranslation();
  const dispatch = useAppDispatch();
  const removal = useAppSelector((root: RootState) => root.panelOrgs.coverRemoval);
  /** Diyalog açıkken personelin AÇTIĞI andaki kapak; istek bu kimliği taşır. */
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [failedUrl, setFailedUrl] = useState<string | null>(null);

  useEffect(() => {
    dispatch(coverRemovalReset());
    setRemovingId(null);
  }, [dispatch, org.id]);

  const coverMediaId = org.coverMediaId;
  const url = org.coverImageUrl;
  const showImage = url !== null && failedUrl !== url;

  let preview;
  if (coverMediaId === null) {
    preview = <p className={styles.contractNone}>{t.t('admin.orgs.cover.none')}</p>;
  } else if (showImage) {
    preview = (
      <img
        className={styles.coverPreview}
        style={COVER_RATIO}
        src={url}
        alt={t.t('admin.orgs.cover.previewAlt', { name: org.name })}
        onError={() => setFailedUrl(url)}
      />
    );
  } else {
    preview = (
      <div className={styles.coverPlaceholder} style={COVER_RATIO}>
        <p className={styles.coverPlaceholderText}>
          {url === null
            ? t.t('admin.orgs.cover.previewPending')
            : t.t('admin.orgs.cover.previewError')}
        </p>
      </div>
    );
  }

  const notice = removingId === null ? removal.notice : null;

  return (
    <div className={styles.card}>
      <h2 className={styles.cardTitle}>{t.t('admin.orgs.cover.title')}</h2>
      {preview}
      {coverMediaId !== null ? (
        <PermissionSurfaceGate
          permission={subSurfacePermission('panel:organization-cover-removal')}
        >
          <div className={styles.cardActions}>
            <Button variant="dangerOutline" size="sm" onClick={() => setRemovingId(coverMediaId)}>
              {t.t('admin.orgs.cover.remove')}
            </Button>
          </div>
        </PermissionSurfaceGate>
      ) : null}
      {notice ? (
        <p
          className={[styles.notice, notice === 'removed' ? '' : styles.noticeWarn]
            .filter(Boolean)
            .join(' ')}
          role="status"
        >
          {t.t(`admin.orgs.cover.${notice}`)}
        </p>
      ) : null}
      {org.lastCoverRemoval ? <LastCoverRemoval removal={org.lastCoverRemoval} /> : null}
      {removingId !== null ? (
        <RemoveCoverDialog
          organizationId={org.id}
          organizationName={org.name}
          coverMediaId={removingId}
          onClose={() => setRemovingId(null)}
        />
      ) : null}
    </div>
  );
}

function LastCoverRemoval({ removal }: { removal: PanelOrganizationCoverRemovalDto }) {
  const t = useTranslation();
  const titleId = useId();
  const name = removal.removedBy.displayName ?? t.t('admin.orgs.owner.removedAccount');
  return (
    <section className={styles.coverRemoval} aria-labelledby={titleId}>
      <h3 id={titleId} className={styles.coverRemovalTitle}>
        {t.t('admin.orgs.cover.lastRemovalTitle')}
      </h3>
      <div className={styles.coverRemovalMeta}>
        <span className={styles.metaPerson}>
          <Avatar name={name} size="xs" />
          {t.t('admin.orgs.cover.lastRemovalBy', { name })}
        </span>
        <time dateTime={removal.removedAt}>
          {t.formatDate(removal.removedAt, numericDateTimeOptions(t.locale))}
        </time>
      </div>
      <p className={styles.coverRemovalReason}>{removal.reason}</p>
    </section>
  );
}
